import Dexie, { type EntityTable } from 'dexie'
import { lineTotal, QTY_UNIT, type Vnd } from './money'

/**
 * Cơ sở dữ liệu ngay trên máy. Bán hàng ghi vào đây trước, đẩy lên máy chủ sau.
 * Quán ở chợ rớt mạng liên tục — mất mạng mà không bán được là mất khách ngay ngày đầu.
 */

export type OrderStatus = 'Open' | 'Paid' | 'Void'
export type PayMethod = 'Cash' | 'Transfer'
export type DiscountKind = 'Percent' | 'Amount'

export interface Category {
  id: string
  name: string
  sortOrder: number
}

export interface Product {
  id: string
  categoryId: string | null
  name: string
  price: Vnd
  unit: string
  quickOptions: string[]
  soldByWeight: boolean
  /** Mã in trên bao bì. Món tự pha như cà phê thì để trống. */
  barcode: string | null
  /** Mã ảnh: mã kho dựng sẵn, hoặc "u/..." nếu chủ quán tự tải lên. */
  imageKey: string | null
  /** Những bộ tuỳ chọn áp cho món này. Rỗng nghĩa là món không có ghi chú đặt sẵn. */
  optionSetIds: string[]
  sortOrder: number
}

/**
 * Một bộ ghi chú đặt sẵn: "Đường" gồm ít đường / bình thường / nhiều đường.
 *
 * Khác `quickOptions` của món, và đừng lẫn hai thứ: `quickOptions` là biến thể in lên mặt
 * nút gọi món (M/L, Đá/Nóng) và quyết định đây là dòng hàng nào. Bộ này là ghi chú, chọn
 * sau khi món đã vào giỏ, không đổi giá và không tách dòng.
 */
export interface OptionSet {
  id: string
  name: string
  choices: string[]
  sortOrder: number
}

export interface Zone {
  id: string
  name: string
  sortOrder: number
}

export interface DiningTable {
  id: string
  zoneId: string
  name: string
  sortOrder: number
  isTakeaway: boolean
  icon: string | null
  color: string | null
  gridX: number
  gridY: number
  gridW: number
  gridH: number
  seats: number
  reservedAt: string | null
  reservedName: string | null
  reservedPartySize: number
  reservedNote: string | null
}

/**
 * Chương trình khuyến mãi đặt sẵn thành nút bấm ở màn hình thu tiền.
 * Máy chủ là nguồn sự thật — kéo về cùng danh mục để mất mạng vẫn bấm được.
 */
export interface Promotion {
  id: string
  name: string
  kind: DiscountKind
  /** Phần trăm (1–100) hoặc số tiền VND, tuỳ `kind`. */
  value: number
  /** Hoá đơn phải từ ngần này trở lên. 0 là không ràng buộc. */
  minOrderTotal: Vnd
  /** Trần số tiền bớt khi tính theo phần trăm. 0 là không chặn trần. */
  maxDiscount: Vnd
  /** YYYY-MM-DD theo ngày Việt Nam, hoặc null nếu chạy vô thời hạn. */
  startsOn: string | null
  endsOn: string | null
  sortOrder: number
}

export interface ExpenseCategory {
  id: string
  name: string
  emoji: string
  sortOrder: number
}

export interface OrderItem {
  id: string
  productId: string | null
  name: string
  unitPrice: Vnd
  qtyMilli: number
  lineTotal: Vnd
  optionLabel: string | null
  note: string | null
}

/**
 * Kết quả hoá đơn điện tử của một đơn, chụp lại ngay trên máy.
 *
 * Giữ ở đây để in lại tờ bill lúc mất mạng vẫn ra đúng số hoá đơn và mã cơ quan thuế —
 * khách quay lại xin bản in thì không ai đi hỏi máy chủ mới in được.
 *
 * Máy khách <b>không bao giờ tự điền mấy ô này</b>. Chúng chỉ đến từ máy chủ, vì số hoá
 * đơn là thứ nhà cung cấp cấp phát, không phải thứ máy quầy tự nghĩ ra.
 */
export interface OrderEInvoice {
  status: 'Pending' | 'Issued' | 'Failed' | 'Cancelled'
  templateCode: string | null
  series: string | null
  invoiceNo: string | null
  taxAuthorityCode: string | null
  lookupUrl: string | null
  issuedAt: string | null
  error: string | null
}

export interface Order {
  id: string
  code: string
  tableId: string | null
  tableName: string | null
  status: OrderStatus
  openedAt: string
  paidAt: string | null
  subtotal: Vnd
  discount: Vnd
  /** Câu in lên bill: "Giảm 10%" hoặc tên chương trình khuyến mãi. */
  discountLabel: string | null
  /** Phần trăm đã bớt, 0 nghĩa là bớt thẳng một số tiền. Thêm món thì số tiền bớt chạy theo. */
  discountPercent: number
  promotionId: string | null
  total: Vnd
  paymentMethod: PayMethod | null
  amountGiven: Vnd
  changeGiven: Vnd
  customerName: string | null
  note: string | null
  deviceId: string
  clientCreatedAt: string
  items: OrderItem[]
  /** Huỷ hoá đơn đã thu tiền: không xoá dòng, không sửa số tiền, chỉ đánh dấu. */
  voidedAt: string | null
  voidReason: string | null
  /** Do máy chủ trả về, không phải do máy này tự điền. Trống nghĩa là quán chưa bật. */
  einvoice?: OrderEInvoice
  /** 0 = chưa đẩy lên máy chủ. Dexie không đánh chỉ mục được kiểu boolean. */
  synced: 0 | 1
}

export interface Expense {
  id: string
  categoryId: string | null
  categoryName: string
  emoji: string
  amount: Vnd
  note: string | null
  photoUrl: string | null
  spentAt: string
  deviceId: string
  /** Chỉ khoản trả tiền mặt mới rút két, nên chốt ca mới trừ đúng. */
  paymentMethod: PayMethod
  synced: 0 | 1
}

export interface Shift {
  id: string
  openedAt: string
  closedAt: string | null
  openingCash: Vnd
  cashSales: Vnd
  cashExpense: Vnd
  expectedCash: Vnd
  countedCash: Vnd
  cashDiff: Vnd
  note: string | null
  deviceId: string
  synced: 0 | 1
}

export interface KvRow {
  key: string
  value: unknown
}

class PosDb extends Dexie {
  kv!: EntityTable<KvRow, 'key'>
  categories!: EntityTable<Category, 'id'>
  products!: EntityTable<Product, 'id'>
  zones!: EntityTable<Zone, 'id'>
  diningTables!: EntityTable<DiningTable, 'id'>
  expenseCategories!: EntityTable<ExpenseCategory, 'id'>
  promotions!: EntityTable<Promotion, 'id'>
  optionSets!: EntityTable<OptionSet, 'id'>
  orders!: EntityTable<Order, 'id'>
  expenses!: EntityTable<Expense, 'id'>
  shifts!: EntityTable<Shift, 'id'>

  constructor() {
    super('sigopos')
    this.version(6).stores({
      kv: 'key',
      categories: 'id, sortOrder',
      products: 'id, categoryId, barcode, sortOrder',
      zones: 'id, sortOrder',
      diningTables: 'id, zoneId, sortOrder',
      expenseCategories: 'id, sortOrder',
      promotions: 'id, sortOrder',
      optionSets: 'id, sortOrder',
      orders: 'id, status, tableId, paidAt, synced, openedAt',
      expenses: 'id, spentAt, synced',
      shifts: 'id, openedAt, closedAt, synced',
    })
  }
}

export const db = new PosDb()

// ───────────────────────── kv ─────────────────────────

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const row = await db.kv.get(key)
  return row?.value as T | undefined
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await db.kv.put({ key, value })
}

/**
 * Mã máy. Đi vào số hoá đơn để hai máy cùng quán bán offline không đụng số nhau,
 * và để tra ra đơn này bán ở máy nào khi cần đối chiếu.
 */
export async function deviceId(): Promise<string> {
  let id = await kvGet<string>('deviceId')
  if (!id) {
    id = crypto.randomUUID().slice(0, 8).toUpperCase()
    await kvSet('deviceId', id)
  }
  return id
}

// ───────────────────────── đơn hàng ─────────────────────────

const two = (n: number) => String(n).padStart(2, '0')

/** HD260908-A1F0042 — ngày + mã máy + số thứ tự trong ngày của chính máy đó. */
export async function nextOrderCode(): Promise<string> {
  const dev = await deviceId()
  const now = new Date()
  const stamp = `${String(now.getFullYear()).slice(2)}${two(now.getMonth() + 1)}${two(now.getDate())}`

  const key = `seq:${stamp}`
  const current = (await kvGet<number>(key)) ?? 0
  const next = current + 1
  await kvSet(key, next)

  return `HD${stamp}-${dev.slice(0, 3)}${String(next).padStart(4, '0')}`
}

export const recalc = (items: OrderItem[]): Vnd =>
  items.reduce((sum, i) => sum + i.lineTotal, 0)

/** Chỉ cần id và tên bàn — nhận hẹp lại để nơi gọi khỏi phải dựng cả đối tượng bàn. */
export type TableRef = Pick<DiningTable, 'id' | 'name'>

export async function openOrderForTable(table: TableRef): Promise<Order> {
  const existing = await db.orders.where({ tableId: table.id, status: 'Open' }).first()
  if (existing) return existing

  const now = new Date().toISOString()
  const order: Order = {
    id: crypto.randomUUID(),
    code: await nextOrderCode(),
    tableId: table.id,
    tableName: table.name,
    status: 'Open',
    openedAt: now,
    paidAt: null,
    subtotal: 0,
    discount: 0,
    discountLabel: null,
    discountPercent: 0,
    promotionId: null,
    total: 0,
    paymentMethod: null,
    amountGiven: 0,
    changeGiven: 0,
    customerName: null,
    note: null,
    deviceId: await deviceId(),
    clientCreatedAt: now,
    items: [],
    voidedAt: null,
    voidReason: null,
    synced: 0,
  }
  await db.orders.add(order)
  return order
}

/**
 * Số tiền bớt của một hoá đơn, sau khi đã chặn hai đầu.
 *
 * Bớt theo phần trăm thì tính lại theo tạm tính hiện tại, không giữ con số cũ: khách gọi
 * thêm hai ly nữa mà số tiền bớt đứng nguyên thì hoá đơn sai theo cách không ai để ý.
 */
export function resolveDiscount(
  subtotal: Vnd,
  discount: Vnd,
  discountPercent: number,
): Vnd {
  const raw = discountPercent > 0 ? Math.round((subtotal * discountPercent) / 100) : discount
  return Math.min(Math.max(0, raw), Math.max(0, subtotal))
}

/**
 * Tính lại số tiền bớt sau khi đơn đổi món.
 *
 * Đơn đang chạy một chương trình thì hỏi lại chính chương trình đó — có thế mới giữ đúng
 * trần và mức hoá đơn tối thiểu. Chương trình bị bỏ giữa chừng thì thôi, giữ nguyên số tiền
 * đã bớt: khách đã được hứa rồi, không rút lại giữa bữa.
 */
async function recalcDiscount(order: Order, subtotal: Vnd): Promise<Vnd> {
  if (order.promotionId) {
    const promo = await db.promotions.get(order.promotionId)
    if (promo) return promotionDiscount(promo, subtotal)
  }
  return resolveDiscount(subtotal, order.discount ?? 0, order.discountPercent ?? 0)
}

export async function saveItems(orderId: string, items: OrderItem[]): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order) return

  const subtotal = recalc(items)
  const discount = await recalcDiscount(order, subtotal)

  await db.orders.update(orderId, {
    items,
    subtotal,
    discount,
    // Bớt 0 đồng thì gỡ luôn cái nhãn. Còn chữ "Giảm 10%" trên bill mà không bớt đồng nào
    // là nói với khách một điều không đúng.
    discountLabel: discount > 0 ? (order.discountLabel ?? null) : null,
    total: subtotal - discount,
    synced: 0,
  })
}

/**
 * Bớt tiền cho hoá đơn. Truyền `null` là bỏ giảm giá.
 *
 * Một chỗ duy nhất ghi bốn ô giảm giá, để không bao giờ có cảnh còn cái nhãn "Giảm 10%"
 * trên bill mà số tiền đã bị gỡ ra.
 */
export async function applyDiscount(
  orderId: string,
  d: { amount?: Vnd; percent?: number; label: string; promotionId?: string | null } | null,
): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.status !== 'Open') return

  const percent = d?.percent ?? 0
  const discount = d ? resolveDiscount(order.subtotal, d.amount ?? 0, percent) : 0

  await db.orders.update(orderId, {
    discount,
    discountLabel: d && discount > 0 ? d.label : null,
    discountPercent: discount > 0 ? percent : 0,
    promotionId: discount > 0 ? (d?.promotionId ?? null) : null,
    total: order.subtotal - discount,
    synced: 0,
  })
}

export function addToItems(items: OrderItem[], p: Product, option: string | null): OrderItem[] {
  const key = (i: OrderItem) => `${i.productId}|${i.optionLabel ?? ''}`
  const target = `${p.id}|${option ?? ''}`
  const found = items.find((i) => key(i) === target)

  if (found) {
    return items.map((i) =>
      key(i) === target
        ? { ...i, qtyMilli: i.qtyMilli + QTY_UNIT, lineTotal: lineTotal(i.unitPrice, i.qtyMilli + QTY_UNIT) }
        : i,
    )
  }

  return [
    ...items,
    {
      id: crypto.randomUUID(),
      productId: p.id,
      name: p.name,
      unitPrice: p.price,
      qtyMilli: QTY_UNIT,
      lineTotal: p.price,
      optionLabel: option,
      note: null,
    },
  ]
}

export function changeQty(items: OrderItem[], itemId: string, deltaMilli: number): OrderItem[] {
  return items
    .map((i) => {
      if (i.id !== itemId) return i
      const qty = i.qtyMilli + deltaMilli
      return { ...i, qtyMilli: qty, lineTotal: lineTotal(i.unitPrice, qty) }
    })
    .filter((i) => i.qtyMilli > 0)
}

export async function payOrder(
  orderId: string,
  method: PayMethod,
  amountGiven: Vnd,
): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order) return

  await db.orders.update(orderId, {
    status: 'Paid',
    paidAt: new Date().toISOString(),
    paymentMethod: method,
    amountGiven,
    changeGiven: method === 'Cash' ? Math.max(0, amountGiven - order.total) : 0,
    synced: 0,
  })
}

/**
 * Ghi kết quả hoá đơn điện tử máy chủ trả về.
 *
 * Không đụng tới cờ `synced`: đây là dữ liệu đi <b>xuống</b>, đánh dấu chưa đồng bộ là đẩy
 * ngược một đơn đã thanh toán lên lại, mà đơn đã thanh toán thì bất biến.
 */
export async function saveEInvoice(orderId: string, einvoice: OrderEInvoice): Promise<void> {
  await db.orders.update(orderId, { einvoice })
}

// ───────────────────────── khuyến mãi ─────────────────────────

/** Ngày hôm nay theo lịch máy, dạng YYYY-MM-DD — cùng dạng với ngày chạy chương trình. */
const todayIso = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
}

/** Chương trình này bớt được bao nhiêu cho hoá đơn ngần này. 0 nghĩa là chưa đủ điều kiện. */
export function promotionDiscount(p: Promotion, subtotal: Vnd): Vnd {
  if (subtotal <= 0 || subtotal < p.minOrderTotal) return 0

  let raw = p.kind === 'Percent' ? Math.round((subtotal * p.value) / 100) : p.value
  if (p.kind === 'Percent' && p.maxDiscount > 0) raw = Math.min(raw, p.maxDiscount)

  return Math.min(Math.max(0, raw), subtotal)
}

/**
 * Chương trình đang chạy hôm nay, đã xếp theo thứ tự chủ quán đặt.
 *
 * Trả về cả chương trình chưa đủ điều kiện, kèm số tiền bớt bằng 0: người bán cần thấy
 * "hoá đơn từ 100k mới được giảm" chứ không phải thấy nút biến mất mà không hiểu vì sao.
 */
export async function runningPromotions(subtotal: Vnd): Promise<{ promo: Promotion; discount: Vnd }[]> {
  const today = todayIso()
  const all = await db.promotions.orderBy('sortOrder').toArray()

  return all
    .filter((p) => (!p.startsOn || today >= p.startsOn) && (!p.endsOn || today <= p.endsOn))
    .map((promo) => ({ promo, discount: promotionDiscount(promo, subtotal) }))
}

/** Huỷ đơn chưa thanh toán — khách đổi ý trước khi trả tiền. */
export async function voidOrder(orderId: string): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.status === 'Paid') return
  await db.orders.update(orderId, { status: 'Void', synced: 0 })
}

/**
 * Huỷ hoá đơn <b>đã thu tiền</b> — rung nhầm bill, khách trả lại hàng.
 *
 * Không xoá dòng và không sửa một con số nào: chỉ đánh dấu đã huỷ kèm lý do và giờ huỷ.
 * Xoá mất một hoá đơn là mất luôn cách chứng minh mình đúng khi khách khiếu nại.
 * Làm được cả lúc mất mạng; máy chủ nhận đúng một chuyển trạng thái này.
 */
export async function voidPaidOrder(orderId: string, reason: string): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.voidedAt) return

  await db.orders.update(orderId, {
    status: 'Void',
    voidedAt: new Date().toISOString(),
    voidReason: reason.trim(),
    synced: 0,
  })
}

/** Hoá đơn trong một ngày, đọc thẳng từ máy nên mất mạng vẫn tra được. */
export async function ordersOnDay(day: Date): Promise<Order[]> {
  const start = new Date(day)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start.getTime() + 86_400_000)

  return db.orders
    .where('paidAt')
    .between(start.toISOString(), end.toISOString(), true, false)
    .reverse()
    .sortBy('paidAt')
}

/** Chuyển bàn: giữ nguyên đơn, đổi chỗ ngồi. Dùng khi khách đổi chỗ giữa chừng. */
export async function moveOrder(orderId: string, to: TableRef): Promise<void> {
  await db.orders.update(orderId, { tableId: to.id, tableName: to.name, synced: 0 })
}

/** Gộp bàn: dồn món của bàn nguồn sang bàn đích rồi đóng đơn nguồn. */
export async function mergeOrders(fromId: string, intoId: string): Promise<void> {
  const [from, into] = await Promise.all([db.orders.get(fromId), db.orders.get(intoId)])
  if (!from || !into || from.status !== 'Open' || into.status !== 'Open') return

  const items = [...into.items, ...from.items]
  await saveItems(intoId, items)
  await db.orders.update(fromId, { status: 'Void', note: `Gộp vào ${into.tableName ?? into.code}`, synced: 0 })
}

// ───────────────────────── chi ─────────────────────────

export async function addExpense(
  cat: { id: string | null; name: string; emoji: string },
  amount: Vnd,
  note?: string,
  paymentMethod: PayMethod = 'Cash',
): Promise<void> {
  await db.expenses.add({
    id: crypto.randomUUID(),
    categoryId: cat.id,
    categoryName: cat.name,
    emoji: cat.emoji,
    amount,
    note: note ?? null,
    photoUrl: null,
    spentAt: new Date().toISOString(),
    deviceId: await deviceId(),
    paymentMethod,
    synced: 0,
  })
}

// ───────────────────────── ca làm ─────────────────────────

export interface ShiftTally {
  cashSales: Vnd
  cashExpense: Vnd
  transferSales: Vnd
  orderCount: number
  expectedCash: Vnd
}

/**
 * Ca đang mở của quán.
 *
 * Sắp xếp theo giờ mở rồi lấy cái sớm nhất, không lấy bừa: nếu hai máy lỡ cùng mở ca
 * trong lúc chưa kéo được dữ liệu của nhau, cả hai phải chọn ra CÙNG một ca — không thì
 * mỗi máy tính một số tiền phải có trong két.
 */
export async function currentShift(): Promise<Shift | undefined> {
  const open = await db.shifts.filter((s) => s.closedAt === null).toArray()
  return open.sort((a, b) => a.openedAt.localeCompare(b.openedAt))[0]
}

export async function openShift(openingCash: Vnd): Promise<Shift> {
  const already = await currentShift()
  if (already) return already

  const shift: Shift = {
    id: crypto.randomUUID(),
    openedAt: new Date().toISOString(),
    closedAt: null,
    openingCash,
    cashSales: 0,
    cashExpense: 0,
    expectedCash: openingCash,
    countedCash: 0,
    cashDiff: 0,
    note: null,
    deviceId: await deviceId(),
    synced: 0,
  }
  await db.shifts.add(shift)
  return shift
}

/**
 * Tính lại số liệu của ca từ dữ liệu trên máy này.
 *
 * Tính theo khoảng thời gian ca mở chứ không gắn khoá ca vào từng đơn — làm vậy thì
 * đơn bán lúc mất mạng vẫn rơi đúng ca mà không cần biết ca nào đang mở.
 */
export async function tallyShift(shift: Shift): Promise<ShiftTally> {
  const from = shift.openedAt
  const to = shift.closedAt ?? new Date().toISOString()

  const paid = await db.orders
    .where('paidAt')
    .between(from, to, true, true)
    .filter((o) => o.status === 'Paid')
    .toArray()

  const spends = await db.expenses.where('spentAt').between(from, to, true, true).toArray()

  const cashSales = paid.filter((o) => o.paymentMethod === 'Cash').reduce((s, o) => s + o.total, 0)
  const transferSales = paid
    .filter((o) => o.paymentMethod === 'Transfer')
    .reduce((s, o) => s + o.total, 0)

  // Phiếu chi cũ chưa có trường này thì coi như tiền mặt — đúng với thực tế đi chợ.
  const cashExpense = spends
    .filter((e) => (e.paymentMethod ?? 'Cash') === 'Cash')
    .reduce((s, e) => s + e.amount, 0)

  return {
    cashSales,
    cashExpense,
    transferSales,
    orderCount: paid.length,
    expectedCash: shift.openingCash + cashSales - cashExpense,
  }
}

export async function closeShift(
  shift: Shift,
  countedCash: Vnd,
  note?: string,
): Promise<Shift> {
  const t = await tallyShift(shift)

  const closed: Shift = {
    ...shift,
    closedAt: new Date().toISOString(),
    cashSales: t.cashSales,
    cashExpense: t.cashExpense,
    expectedCash: t.expectedCash,
    countedCash,
    cashDiff: countedCash - t.expectedCash,
    note: note ?? null,
    synced: 0,
  }
  await db.shifts.put(closed)
  return closed
}

// ───────────────────────── tổng hợp trong ngày ─────────────────────────

export interface DayTotals {
  revenue: Vnd
  expense: Vnd
  profit: Vnd
  orderCount: number
  cash: Vnd
  transfer: Vnd
}

const startOfDay = (d = new Date()) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export async function dayTotals(day: Date = new Date()): Promise<DayTotals> {
  const from = startOfDay(day).toISOString()
  const to = new Date(startOfDay(day).getTime() + 86_400_000).toISOString()

  const paid = await db.orders
    .where('paidAt')
    .between(from, to, true, false)
    .filter((o) => o.status === 'Paid')
    .toArray()

  const spends = await db.expenses.where('spentAt').between(from, to, true, false).toArray()

  const revenue = paid.reduce((s, o) => s + o.total, 0)
  const expense = spends.reduce((s, e) => s + e.amount, 0)

  return {
    revenue,
    expense,
    profit: revenue - expense,
    orderCount: paid.length,
    cash: paid.filter((o) => o.paymentMethod === 'Cash').reduce((s, o) => s + o.total, 0),
    transfer: paid.filter((o) => o.paymentMethod === 'Transfer').reduce((s, o) => s + o.total, 0),
  }
}

export async function topProductsToday(limit = 6) {
  const from = startOfDay().toISOString()
  const to = new Date(startOfDay().getTime() + 86_400_000).toISOString()

  const paid = await db.orders
    .where('paidAt')
    .between(from, to, true, false)
    .filter((o) => o.status === 'Paid')
    .toArray()

  const acc = new Map<string, { name: string; qtyMilli: number; revenue: Vnd }>()
  for (const order of paid) {
    for (const item of order.items) {
      const row = acc.get(item.name) ?? { name: item.name, qtyMilli: 0, revenue: 0 }
      row.qtyMilli += item.qtyMilli
      row.revenue += item.lineTotal
      acc.set(item.name, row)
    }
  }

  return [...acc.values()].sort((a, b) => b.revenue - a.revenue).slice(0, limit)
}

/**
 * Tra sản phẩm theo mã vừa quét. Tra trên máy chứ không hỏi máy chủ: máy scan bắn
 * mã giữa lúc đông khách, đợi mạng là mất nhịp — và quán mất mạng vẫn phải quét được.
 */
export async function findByBarcode(code: string): Promise<Product | undefined> {
  return db.products.where('barcode').equals(code).first()
}

/** Xoá sạch dữ liệu máy này — dùng khi đăng xuất, hoặc khi sang tay quán cho người khác. */
export async function wipeLocal(): Promise<void> {
  await Promise.all([
    db.kv.clear(),
    db.categories.clear(),
    db.products.clear(),
    db.zones.clear(),
    db.diningTables.clear(),
    db.expenseCategories.clear(),
    db.promotions.clear(),
    db.optionSets.clear(),
    db.orders.clear(),
    db.expenses.clear(),
    db.shifts.clear(),
  ])
}
